/* eslint-disable @typescript-eslint/no-unused-vars */
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id, Doc } from '@/convex/_generated/dataModel';
import { useAuth } from './useAuth';
import { useMemo } from 'react';

export interface UserInfo {
	email: string;
	name: string;
	walletAddress?: string;
}

export function useUserInfo() {
	const { user } = useAuth();

	const users = useQuery(
		api.users.list,
		user?.id && user?.initialApiKey
			? {
					requestingUserId: user.id as unknown as Id<'users'>,
					apiKey: user.initialApiKey,
				}
			: 'skip'
	);

	const getUserInfo = useMemo(() => {
		if (!users) {
			return (userId: Id<'users'> | string) => ({
				email: 'Unknown',
				name: 'Unknown User',
				walletAddress: undefined,
			});
		}

		const userMap = new Map<string, UserInfo>(
			users.map((u: Doc<'users'>) => [
				u._id.toString(),
				{
					email: u.email || 'Unknown',
					name: u.name || 'Unknown User',
					walletAddress: u.walletAddress,
				},
			])
		);

		return (userId: Id<'users'> | string): UserInfo => {
			let key: string;
			if (typeof userId === 'string') {
				key = userId;
			} else {
				// Handle Id<'users'> type
				const idObj = userId as { toString(): string };
				key = idObj.toString();
			}
			const userInfo = userMap.get(key);
			if (userInfo) {
				return userInfo;
			}
			return {
				email: 'Unknown',
				name: 'Unknown User',
				walletAddress: undefined,
			};
		};
	}, [users]);

	return {
		users,
		getUserInfo,
	};
}
